import { useState } from 'react'
import { Clock, TrendingUp, Users, Shield, ArrowRight, CheckCircle2 } from 'lucide-react'
import { motion } from 'framer-motion'
import Button from '../ui/Button'
import FormField from '../ui/FormField'
import useFormValidation from '../../hooks/useFormValidation'

const HeroSection = () => {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const { values, errors, handleChange, handleBlur, validateAll, reset } = useFormValidation(
    {
      name: '',
      email: '',
      phone: '',
      company: ''
    },
    {
      name: {
        required: true,
        minLength: 3,
        message: 'Nome deve ter no mínimo 3 caracteres'
      },
      email: {
        required: true,
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'E-mail inválido'
      },
      phone: {
        required: true,
        pattern: /^\(?[1-9]{2}\)? ?(?:[2-8]|9[1-9])[0-9]{3}\-?[0-9]{4}$/,
        message: 'Telefone inválido'
      },
      company: {
        required: true,
        minLength: 2,
        message: 'Nome da empresa deve ter no mínimo 2 caracteres'
      }
    }
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateAll()) return

    setSubmitStatus('loading')

    try {
      await new Promise(resolve => setTimeout(resolve, 1500))
      setSubmitStatus('success')
      reset()
      
      setTimeout(() => setSubmitStatus('idle'), 5000)
    } catch (error) {
      setSubmitStatus('error')
      setTimeout(() => setSubmitStatus('idle'), 5000)
    }
  }

  const features = [
    { icon: Clock, text: 'Banco de horas automatizado' },
    { icon: TrendingUp, text: 'Relatórios estratégicos' },
    { icon: Users, text: 'Gestão em tempo real' },
    { icon: Shield, text: 'Cerca virtual e segurança' }
  ]

  return (
    <section id="hero" className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
      
      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-primary-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-bold mb-6"
            >
              <CheckCircle2 className="w-4 h-4 mr-2 text-primary-400" />
              Usado por +1.000 empresas
            </motion.div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
              Controle de Ponto{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-accent-400">
                Inteligente
              </span>{' '}
              para sua Empresa
            </h1>

            {/* Subheadline */}
            <p className="text-xl text-white/90 font-medium mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              Automatize a gestão de ponto com nossa solução completa de RH Digital. 
              Conforme portaria 671 e LGPD.
            </p>

            {/* Features List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => {
                const Icon = feature.icon
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="flex items-center gap-3 text-white"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-500 shadow-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-semibold">{feature.text}</span>
                  </motion.div>
                )
              })}
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-6 justify-center lg:justify-start text-sm text-white/80 font-medium">
              {['Teste grátis por 14 dias', 'Sem cartão de crédito', 'Suporte 24/7'].map((item) => (
                <div key={item} className="flex items-center">
                  <CheckCircle2 className="w-5 h-5 text-primary-400 mr-2" />
                  {item}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 border border-gray-200 dark:border-gray-800">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Solicite uma Demonstração Gratuita
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Preencha o formulário e nossa equipe entrará em contato
                </p>
              </div>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm"
                >
                  ✓ Mensagem enviada com sucesso! Entraremos em contato em breve.
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm"
                >
                  ✗ Erro ao enviar mensagem. Tente novamente.
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <FormField
                  label="Nome Completo"
                  name="name"
                  placeholder="Seu nome"
                  required
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.name}
                />

                <FormField
                  label="E-mail"
                  name="email"
                  type="email"
                  placeholder="seu@email.com"
                  required
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.email}
                />

                <FormField
                  label="Telefone"
                  name="phone"
                  type="tel"
                  placeholder="(11) 98765-4321"
                  required
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.phone}
                />

                <FormField
                  label="Empresa"
                  name="company"
                  placeholder="Nome da sua empresa"
                  required
                  value={values.company}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.company}
                />

                <Button
                  type="submit"
                  size="lg"
                  disabled={submitStatus === 'loading'}
                  className="w-full group"
                >
                  {submitStatus === 'loading' ? (
                    <>
                      <span className="spinner mr-2"></span>
                      Enviando...
                    </>
                  ) : (
                    <>
                      Solicitar Demonstração
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
                  Seus dados estão seguros e protegidos conforme a LGPD
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection
